// src/admin/pages/ClaimUrlPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import ModalSearchBar from '../components/ModalSearchBar';
import LoadingView from '../../shared/components/LoadingView';
import useAdminAssets from '../hooks/useAdminAssets';
import useAdminWorkflows from '../hooks/useAdminWorkflows';
import { useShortUrlActions } from '../hooks/useShortUrlBatches';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import { formatAddress, hasAddress } from '../../shared/utils/formatAddress';
import { API_URL } from '../../config/env';

/**
 * Page for claiming an unclaimed short URL to an asset.
 * This page is navigated to when an admin scans an unclaimed QR code.
 */
export default function ClaimUrlPage() {
  useDocumentTitle('Claim Access URL – Maintella');
  
  const { code } = useParams();
  const navigate = useNavigate();
  const { data: assets, refresh: refreshAssets } = useAdminAssets();
  const { data: workflows, refresh: refreshWorkflows } = useAdminWorkflows();
  const { claimUrl } = useShortUrlActions();

  const [shortUrl, setShortUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch the short URL details
  useEffect(() => {
    const fetchShortUrl = async () => {
      setLoading(true);
      try {
        const res = await fetchWithAuth(`${API_URL}/v1/admin/short_urls/unclaimed`);
        if (!res.ok) throw new Error('Failed to fetch unclaimed URLs');
        const data = await res.json();
        const found = data.find(url => url.code === code);
        if (found) {
          setShortUrl(found);
        } else {
          setError('This short URL is not available for claiming. It may already be claimed or invalidated.');
        }
      } catch (err) {
        console.error('Error fetching short URL:', err);
        setError(err.message || 'Failed to load short URL details');
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchShortUrl();
      refreshAssets();
      refreshWorkflows();
    }
  }, [code, refreshAssets, refreshWorkflows]);

  // Filter assets by search
  const filteredAssets = useMemo(() => {
    if (!search.trim()) return assets;
    const q = search.toLowerCase();
    return assets.filter(a =>
      [a.asset_name, a.asset_make, a.asset_model, a.asset_ext_id, a.street_address, a.city]
        .some(field => String(field || '').toLowerCase().includes(q))
    );
  }, [assets, search]);

  const userWorkflowOptions = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state === 'published'),
    [workflows]
  );
  const draftUserWorkflowCount = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state !== 'published').length,
    [workflows]
  );

  const handleClaim = async () => {
    if (!selectedAssetId || !shortUrl) return;
    setIsSubmitting(true);
    try {
      await claimUrl(shortUrl.id, selectedAssetId, {
        workflowId: selectedWorkflowId || null,
      });
      navigate('/admin/short-urls', {
        state: { message: `Successfully claimed ${code} to an asset.` }
      });
    } catch (err) {
      alert(err.message || 'Failed to claim URL');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/short-urls');
  };

  // Loading state
  if (loading) {
    return (
      <>
        <ObjectTopBar title="Claim Access URL" />
        <LoadingView
          symbolClass="bi bi-qr-code"
          title="Loading access URL"
          subtitle="Please wait while we fetch short URL details."
          className="py-5"
        />
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <ObjectTopBar title="Claim Access URL" />
        <div className="container py-4">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-exclamation-triangle display-1 text-warning mb-3 d-block" />
              <h5>Unable to Claim URL</h5>
              <p className="text-muted mb-4">{error}</p>
              <button className="btn btn-primary" onClick={handleCancel}>
                Back to Access URLs
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ObjectTopBar title="Claim Access URL" />
      <div className="container py-4">
        <div className="card">
          <div className="card-body">
            {/* Info about the URL being claimed */}
            <div className="alert alert-light border mb-4">
              <div className="d-flex align-items-center gap-3">
                <i className="bi bi-qr-code fs-2" />
                <div>
                  <div className="small text-muted">Claiming short URL</div>
                  <code className="fs-4">{code}</code>
                  {shortUrl?.batch_number && (
                    <div className="small text-muted mt-1">
                      Batch: {shortUrl.batch_number}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <p className="text-muted mb-3">
              Select an asset to associate with this short URL. Once claimed, scanning the QR code will
              direct users to this asset's page where they can start self-service workflows.
            </p>

            {/* Search bar */}
            <ModalSearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search assets..."
            />

            {/* Asset list */}
            <div className="list-group mt-3" style={{ maxHeight: 400, overflowY: 'auto' }}>
              {filteredAssets.length > 0 ? (
                filteredAssets.map(asset => (
                  <button
                    key={asset.id}
                    type="button"
                    className={`list-group-item list-group-item-action d-flex align-items-center gap-3 ${
                      selectedAssetId === asset.id ? 'active' : ''
                    }`}
                    onClick={() => setSelectedAssetId(asset.id)}
                  >
                    <div className="flex-shrink-0">
                      <i className={`bi bi-boombox fs-4 ${selectedAssetId === asset.id ? '' : 'text-muted'}`} />
                    </div>
                    <div className="flex-grow-1 text-start">
                      <div className="fw-semibold">{asset.asset_name || 'Unnamed Asset'}</div>
                      <div className={`small ${selectedAssetId === asset.id ? 'opacity-75' : 'text-muted'}`}>
                        {[asset.asset_make, asset.asset_model].filter(Boolean).join(' · ') || '—'}
                        {hasAddress(asset) && (
                          <span className="ms-2">
                            <i className="bi bi-geo-alt me-1" />
                            {formatAddress(asset, { fallback: '' })}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedAssetId === asset.id && (
                      <i className="bi bi-check-lg" />
                    )}
                  </button>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                  <p className="mb-0">
                    {search ? 'No assets match your search.' : 'No assets available.'}
                  </p>
                </div>
              )}
            </div>

            {/* Optional User Workflow selector */}
            {userWorkflowOptions.length > 0 && (
              <div className="mt-4">
                <label htmlFor="claim-workflow-select" className="form-label">
                  Direct-launch workflow <span className="text-muted">(optional)</span>
                </label>
                <select
                  id="claim-workflow-select"
                  className="form-select"
                  value={selectedWorkflowId}
                  onChange={(e) => setSelectedWorkflowId(e.target.value)}
                >
                  <option value="">— None (regular asset access link) —</option>
                  {userWorkflowOptions.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  When a User Workflow is selected, scanning this QR code starts the
                  workflow directly without prompting visitors for any identity.
                </div>
                {draftUserWorkflowCount > 0 && (
                  <div className="alert alert-warning mt-2 mb-0 py-2 small">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {draftUserWorkflowCount === 1
                      ? '1 additional User Workflow is still in draft and not selectable here. Publish it to use it as a direct-launch workflow.'
                      : `${draftUserWorkflowCount} additional User Workflows are still in draft and not selectable here. Publish them to use them as direct-launch workflows.`}
                  </div>
                )}
              </div>
            )}

            {userWorkflowOptions.length === 0 && draftUserWorkflowCount > 0 && (
              <div className="alert alert-warning mt-4 mb-0 small">
                <i className="bi bi-exclamation-triangle me-2" />
                {draftUserWorkflowCount === 1
                  ? 'A User Workflow exists but is still in draft. Publish it to attach it to this short URL as a direct-launch workflow.'
                  : `${draftUserWorkflowCount} User Workflows exist but are still in draft. Publish at least one of them to attach it to this short URL as a direct-launch workflow.`}
              </div>
            )}

            {/* Action buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleClaim}
                disabled={!selectedAssetId || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <i className="bi bi-link-45deg me-1" />
                    Claim URL to Asset
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
